/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends, typeof as _typeof } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { Filter, Download, Renew } from '@carbon/react/icons';
import { Button } from '@carbon/react';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import { emptyObject } from '../../../../Utilities/propHelpers/defaultProps.js';

var _excluded = ["type", "disabled", "toggleState", "onClick", "iconData", "className"];
var TableToolbarButton = function TableToolbarButton(_ref) {
  var type = _ref.type,
    disabled = _ref.disabled,
    toggleState = _ref.toggleState,
    onClick = _ref.onClick,
    _ref$iconData = _ref.iconData,
    iconData = _ref$iconData === void 0 ? emptyObject : _ref$iconData,
    className = _ref.className,
    others = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var metadata = _objectSpread2({
    filter: {
      css: classnames('cds--table-toolbar-filter-button', {
        'panel-open': toggleState
      }),
      description: t('Filter'),
      icon: Filter
    },
    download: {
      description: t('Download'),
      icon: Download
    },
    refresh: {
      description: t('Refresh'),
      icon: Renew
    }
  }, iconData);
  if (!metadata[type]) return null;
  var _metadata$type = metadata[type],
    css = _metadata$type.css,
    description = _metadata$type.description,
    icon = _metadata$type.icon;
  return /*#__PURE__*/React.createElement(Button, _extends({
    iconDescription: description,
    title: description,
    kind: "ghost",
    size: "md",
    className: classnames('cds--table-toolbar-button', css, className),
    tabIndex: 0,
    disabled: disabled,
    tooltipAlignment: "center",
    tooltipPosition: "top",
    type: "button",
    hasIconOnly: true,
    renderIcon: icon,
    onClick: onClick
  }, others));
};
var createIconDataPropType = function createIconDataPropType() {
  return function (props, propName, componentName) {
    var prop = props[propName]; // eslint-disable-line react/destructuring-assignment

    if (_typeof(prop) === 'object' && Object.keys(prop).some(function (key) {
      return !prop[key].icon || !prop[key].description;
    })) {
      return new Error("Invalid prop ".concat(propName, " supplied to ").concat(componentName, ". Each item in ").concat(propName, " must have both icon and description."));
    }
    return null;
  };
};
var iconDataPropType = createIconDataPropType();
TableToolbarButton.propTypes = {
  /**
   * Provide type to use built-in button types, e.g. `filter`
   */
  type: PropTypes.string.isRequired,
  /**
   * A custom class name to be applied to the order summary total element.
   */
  className: PropTypes.string,
  /**
   * Provide custom icon data if button is not a built-in type
   * Refer to `filter` built-in type for expected format
   */
  iconData: iconDataPropType,
  /**
   * Provide a flag to specify if button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Provide a onClick handler
   */
  onClick: PropTypes.func.isRequired,
  /**
   * Provide a toggle state if button is a toggle and toggle state is needed
   * e.g. `filter` built-in type uses toggleState to differentiate filter panel open/close state
   */
  toggleState: PropTypes.bool
};
var TableToolbarButton$1 = TableToolbarButton;

export { TableToolbarButton$1 as default };
